/**
 * Workspace switcher, test-workspace stripe and plan badge (04 §0, §12, §14; HANDOFF §0.2, §0.16).
 *
 * The workspaces are `GET /me` → `memberships`: each production workspace and its test workspace (a tenant of its
 * own, ADR-025, `kind: "test"`). Switching sends `X-Brevalt-Tenant` from then on, drops every cached query and
 * goes to the dashboard (an open product or case belongs to the other workspace).
 */
import { useTranslation } from "react-i18next";
import type { Me, Membership } from "@brevalt/api-client/customer/model";
import { Chip, TestWorkspaceStripe, useNow, WorkspaceSwitcherPlaceholder } from "@brevalt/ui";
import {
  isTestWorkspace,
  orderWorkspaces,
  trialDaysLeft,
  useSwitchWorkspace,
  type Entitlements,
} from "@brevalt/app-core";
import { useListBillingPlans } from "@brevalt/api-client/customer";

interface SwitcherItem {
  id: string;
  name: string;
  company: string;
  test?: boolean;
}

/** The switcher's workspaces and the current one (none chosen yet: "Choose a workspace"); null without any. */
export function switcherState(
  me: Me,
  t: (k: "shell.production" | "shell.test_workspace" | "shell.choose_a_workspace") => string,
): { items: SwitcherItem[]; current: SwitcherItem } | null {
  const items = switcherItems(me.memberships, t("shell.production"), t("shell.test_workspace"));
  if (items.length === 0) return null;
  const current = items.find((w) => w.id === me.currentTenant?.tenantId) ?? {
    id: "",
    name: t("shell.choose_a_workspace"),
    company: "",
  };
  return { items, current };
}

/**
 * The switcher's look while its chunk loads (#112: the kit's placeholder, out of the Tab order); pointing at it or
 * touching it starts loading the switcher.
 */
export function SwitcherPlaceholder({ me, onIntent }: { me: Me; onIntent: () => void }) {
  const { t } = useTranslation();
  const state = switcherState(me, t);
  if (!state) return null;
  // A press before the switcher arrived isn't lost: it opens the menu as soon as the switcher mounts. On pointerdown,
  // not click: the switcher may replace the placeholder between press and release, and then no click fires.
  const request = () => {
    openRequested = true;
    onIntent();
  };
  return (
    <span className="contents" onPointerDown={request} onClick={request}>
      <WorkspaceSwitcherPlaceholder current={state.current} onIntent={onIntent} />
    </span>
  );
}

let openRequested = false;

/**
 * Whether the placeholder was pressed. Read during render without consuming it: a render can be thrown away and
 * redone (a Suspense retry interrupted by another update, e.g. the deadline pill's tick), and a request consumed by
 * a discarded render was lost (#112 race under load). The switcher clears it once it has mounted.
 */
export function peekSwitcherOpenRequest(): boolean {
  return openRequested;
}

/** True once if the placeholder was pressed (and not yet taken): called by the mounted switcher. */
export function takeSwitcherOpenRequest(): boolean {
  const requested = openRequested;
  openRequested = false;
  return requested;
}

export function switcherItems(memberships: readonly Membership[], production: string, test: string): SwitcherItem[] {
  return orderWorkspaces(memberships).map((m) => {
    if (!isTestWorkspace(m)) return { id: m.tenantId, name: m.tenantName, company: production };
    const parent = memberships.find((p) => p.tenantId === m.parentTenantId);
    return { id: m.tenantId, name: test, company: parent?.tenantName ?? m.tenantName, test: true };
  });
}

/** Several production workspaces and none chosen for this tab yet (`tenant-ambiguous`): the member picks one. */
export function WorkspacePicker({ options }: { options: readonly Membership[] }) {
  const { t } = useTranslation();
  const switchTo = useSwitchWorkspace();
  const items = switcherItems(options, t("shell.production"), t("shell.test_workspace"));
  return (
    <div className="mx-auto max-w-lg space-y-4 px-4 py-12">
      <h1 tabIndex={-1} className="heading text-xl outline-none">
        {t("shell.choose_a_workspace")}
      </h1>
      <p className="text-sm text-text-muted">{t("shell.choose_a_workspace_body")}</p>
      <ul className="card divide-y divide-border">
        {items.map((w) => (
          <li key={w.id}>
            <button
              type="button"
              onClick={() => {
                switchTo(w.id);
              }}
              className="flex min-h-touch w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-surface-2"
            >
              {w.test ? (
                <span aria-hidden className="test-stripe size-3 rounded-xs" />
              ) : (
                <span aria-hidden className="size-2 rounded-full bg-ok" />
              )}
              <span>
                <span className="block font-medium">{w.name}</span>
                <span className="block text-xs text-text-muted">{w.company}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** True when this user has no workspace at all (not a workspace that went away or a choice to make). */
export function needsWorkspace(me: Me): boolean {
  return !me.currentTenant && me.memberships.length === 0;
}

export function currentMembership(me: Me | undefined): Membership | undefined {
  return me?.memberships.find((m) => m.tenantId === me.currentTenant?.tenantId);
}

export function TestStripe() {
  const { t } = useTranslation();
  return <TestWorkspaceStripe>{t("ui.test_workspace_nothing_here_is_sent")}</TestWorkspaceStripe>;
}

/**
 * "Trial · n days left" from day 1 of the trial (04 §14), else the plan's name (from `GET /billing/plans`). The
 * kit's `PlanBadge` takes fixed English plan names only (REQUESTS #50): a `Chip` with the same look.
 */
export function ShellPlanBadge({ entitlements }: { entitlements: Entitlements }) {
  const { t } = useTranslation();
  const now = useNow(undefined, 60_000);
  const trial = entitlements.tenantStatus === "trial" || entitlements.planCode === "trial";
  const plans = useListBillingPlans({ query: { enabled: !trial && !!entitlements.planCode, staleTime: 3_600_000 } });
  if (trial) {
    // After the trial ended the workspace-state banner speaks; no "Trial · 0 days left".
    const days = trialDaysLeft(entitlements.trialEndsAt, now) ?? 0;
    if (days <= 0) return null;
    return (
      <span data-testid="plan-badge">
        <Chip tone="brand" icon={null}>
          {t("ui.trial_trialdaysleft_days_left", { trialDaysLeft: days })}
        </Chip>
      </span>
    );
  }
  const name = plans.data?.plans?.find((p) => p.code === entitlements.planCode)?.name;
  if (!name) return null;
  return (
    <span data-testid="plan-badge">
      <Chip tone="brand" icon={null}>
        {name}
      </Chip>
    </span>
  );
}
